import { useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, CheckCircle2, Loader2, QrCode as QrIcon, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

// URL do serviço Venom-Bot (configurar no .env do frontend)
const VENOM_BOT_URL = process.env.REACT_APP_VENOM_BOT_URL || 'http://localhost:3000';

export default function WhatsAppConnection() {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Conectar WebSocket
    const newSocket = io(VENOM_BOT_URL);
    
    newSocket.on('connect', () => {
      console.log('🔌 Conectado ao Venom-Bot via WebSocket');
    });

    newSocket.on('status', (data) => {
      console.log('📊 Status:', data.status, data.rawStatus ? `(raw: ${data.rawStatus})` : '');
      setConnectionStatus(data.status); 
      
      if (data.attempts) {
        setAttempts(data.attempts);
      }
    });

    newSocket.on('qrcode', (data) => {
      console.log('📱 QR Code recebido');
      setQrCode(data.qr);
      setAttempts(data.attempts || 0);
      toast.info('QR Code gerado! Escaneie com WhatsApp');
    });

    newSocket.on('connected', (data) => {
      console.log('✅ WhatsApp conectado!');
      toast.success(data.message || 'WhatsApp conectado com sucesso!');
      setQrCode(null);
    });

    newSocket.on('message', (data) => {
      console.log('📩 Nova mensagem:', data);
      toast.info(`Nova mensagem de ${data.name}`);
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Desconectado do WebSocket');
    });

    setSocket(newSocket);

    // Verificar status inicial
    checkStatus();

    return () => {
      if (newSocket) {
        newSocket.close();
      }
    };
  }, []);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${VENOM_BOT_URL}/status`);
      setConnectionStatus(res.data.status || 'disconnected');
      
      // Se conectado, limpar QR
      if (res.data.connected) {
        setQrCode(null);
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${VENOM_BOT_URL}/connect`);
      toast.success(res.data.message);
      setConnectionStatus('connecting');
    } catch (error) {
      toast.error('Erro ao iniciar conexão');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${VENOM_BOT_URL}/disconnect`);
      toast.success(res.data.message);
      setConnectionStatus('disconnected');
      setQrCode(null);
    } catch (error) {
      toast.error('Erro ao desconectar');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: <CheckCircle2 className="w-12 h-12 text-green-600" />,
          title: 'WhatsApp Conectado',
          description: 'Seu WhatsApp está conectado e funcionando normalmente',
          color: 'bg-green-50 border-green-200',
          textColor: 'text-green-900'
        };
      case 'starting':
        return {
          icon: <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />,
          title: 'Iniciando Bot...',
          description: 'Aguarde alguns segundos e clique em "Atualizar Status"',
          color: 'bg-blue-50 border-blue-200',
          textColor: 'text-blue-900'
        };
      case 'connecting':
        return {
          icon: <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />,
          title: 'Conectando...',
          description: 'Aguarde enquanto geramos o QR Code',
          color: 'bg-blue-50 border-blue-200',
          textColor: 'text-blue-900'
        };
      case 'qr_ready':
        return {
          icon: <QrIcon className="w-12 h-12 text-orange-600" />,
          title: 'QR Code Pronto',
          description: 'Escaneie o QR Code com seu WhatsApp',
          color: 'bg-orange-50 border-orange-200',
          textColor: 'text-orange-900'
        };
      case 'disconnected':
        return {
          icon: <WifiOff className="w-12 h-12 text-gray-600" />,
          title: 'WhatsApp Desconectado',
          description: 'Clique em "Conectar WhatsApp" para iniciar',
          color: 'bg-gray-50 border-gray-200',
          textColor: 'text-gray-900'
        };
      default:
        return {
          icon: <AlertCircle className="w-12 h-12 text-yellow-600" />,
          title: 'Status Desconhecido',
          description: `Status: ${connectionStatus}. Clique em "Conectar WhatsApp" para iniciar.`,
          color: 'bg-yellow-50 border-yellow-200',
          textColor: 'text-yellow-900'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div data-testid="whatsapp-connection-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Conexão WhatsApp</h1>
        <p className="text-slate-600">Gerencie a conexão do seu WhatsApp com o sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Card */}
        <Card className={`border-2 ${statusInfo.color}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Wifi className="w-6 h-6" />
              Status da Conexão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center text-center space-y-4">
              {statusInfo.icon}
              <div>
                <h3 className={`text-xl font-bold ${statusInfo.textColor}`}>
                  {statusInfo.title}
                </h3>
                <p className={`text-sm mt-1 ${statusInfo.textColor}`}>
                  {statusInfo.description}
                </p>
              </div>

              {attempts > 0 && connectionStatus === 'qr_ready' && (
                <div className="text-sm text-slate-600">
                  Tentativa {attempts} de 4
                </div>
              )}

              <div className="flex gap-3 mt-4">
                {connectionStatus === 'disconnected' && (
                  <Button
                    onClick={handleConnect}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Conectando...
                      </>
                    ) : (
                      <>
                        <Wifi className="w-4 h-4 mr-2" />
                        Conectar WhatsApp
                      </>
                    )}
                  </Button>
                )}

                {connectionStatus === 'connected' && (
                  <Button
                    onClick={handleDisconnect}
                    disabled={loading}
                    variant="destructive"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Desconectando...
                      </>
                    ) : (
                      <>
                        <WifiOff className="w-4 h-4 mr-2" />
                        Desconectar
                      </>
                    )}
                  </Button>
                )}

                <Button
                  onClick={checkStatus}
                  variant="outline"
                >
                  Atualizar Status
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code Card */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <QrIcon className="w-6 h-6" />
              QR Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center min-h-[300px]">
              {qrCode ? (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border-4 border-blue-600">
                    <img 
                      src={qrCode} 
                      alt="QR Code WhatsApp" 
                      className="w-64 h-64"
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="font-semibold text-slate-800">
                      Escaneie com WhatsApp
                    </p>
                    <p className="text-sm text-slate-600">
                      1. Abra o WhatsApp no celular
                    </p>
                    <p className="text-sm text-slate-600">
                      2. Toque em Menu (⋮) → Aparelhos conectados
                    </p>
                    <p className="text-sm text-slate-600">
                      3. Toque em Conectar aparelho
                    </p>
                    <p className="text-sm text-slate-600">
                      4. Aponte a câmera para este QR Code
                    </p>
                  </div>
                </div>
              ) : connectionStatus === 'connected' ? (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="w-24 h-24 text-green-600 mx-auto" />
                  <div>
                    <h3 className="text-xl font-bold text-green-900">
                      WhatsApp Conectado!
                    </h3>
                    <p className="text-slate-600 mt-2">
                      Você já está conectado e pode usar o sistema normalmente
                    </p>
                  </div>
                </div>
              ) : connectionStatus === 'connecting' ? (
                <div className="text-center space-y-4">
                  <Loader2 className="w-24 h-24 text-blue-600 mx-auto animate-spin" />
                  <div>
                    <h3 className="text-xl font-bold text-blue-900">
                      Preparando Conexão...
                    </h3>
                    <p className="text-slate-600 mt-2">
                      O QR Code aparecerá em alguns segundos
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <QrIcon className="w-24 h-24 text-gray-400 mx-auto" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-700">
                      QR Code Não Disponível
                    </h3>
                    <p className="text-slate-600 mt-2">
                      Clique em "Conectar WhatsApp" para gerar o QR Code
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                1
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Conexão Única</h4>
                <p className="text-sm text-blue-800">
                  Você só precisa escanear o QR Code uma vez. A sessão fica salva.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                2
              </div>
              <div>
                <h4 className="font-semibold text-green-900 mb-1">Tempo Real</h4>
                <p className="text-sm text-green-800">
                  Mensagens chegam instantaneamente via WebSocket. Sem atrasos!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                3
              </div>
              <div>
                <h4 className="font-semibold text-purple-900 mb-1">Seguro</h4>
                <p className="text-sm text-purple-800">
                  Toda comunicação é criptografada pelo próprio WhatsApp.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
